import { cva } from 'class-variance-authority';
import React from 'react';
import { forwardRef, ForwardedRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { cn } from '../../../utils';
import { Text } from '../Text/Text';
import { ButtonProps } from './types';

const variants = cva(
  `
    px-6
    rounded-lg
    focus-visible:outline-2
    focus-visible:outline-blue-600
    focus-visible:outline-offset-2
    cursor-pointer
    w-full
    justify-center
    border-2
    border-transparent
`,
  {
    variants: {
      variant: {
        primary: 'bg-yellow-400 hover:bg-yellow-600 text-gray-900',
        secondary: 'bg-gray-800 hover:bg-gray-900 text-white',
        'outlined-dark':
          'border-2 border-gray-800 text-gray-800 hover:bg-gray-900 hover:text-white',
        'outlined-light':
          'border-2 border-white text-white hover:text-gray-800 hover:bg-white hover:border-gray-800',
        text: 'bg-transparent text-blue-700 hover:bg-gray-100 hover:text-blue-800',
        alternate: 'bg-blue-700 hover:bg-blue-800 text-white',
      },
      size: {
        default: 'h-40px',
        large: 'h-56px',
      },
      disabled: {
        true: 'bg-gray-200 text-gray-500 border-0 focus:outline-none hover:bg-gray-200 hover:text-gray-500 cursor-not-allowed',
      },
      isLink: {
        true: 'inline-flex items-center hover:no-underline focus:no-underline cursor-pointer',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default',
    },
  }
);

const iconVariants = cva('w-4 h-4 fill-current hover:fill-current', {
  variants: {
    variant: {
      primary: 'fill-current hover:fill-current',
      secondary: 'fill-white hover:fill-gray-900',
      'outlined-dark': 'fill-gray-800 hover:fill-white',
      'outlined-light': 'fill-current hover:fill-current',
      text: 'fill-current hover:fill-current',
      alternate: 'fill-white hover:fill-gray-200',
    },
    disabled: {
      true: 'fill-gray-500 hover:fill-gray-500',
    },
  },
});

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant,
      className = '',
      size = 'default',
      disabled = false,
      href,
      iconPrefix,
      iconSuffix,
      buttonClassName = '',
      ...rest
    }: ButtonProps,
    ref
  ) => {
    const fontSize = size === 'large' ? 'base' : 'sm';
    const [isFocusedByTab, setIsFocusedByTab] = useState(false);
    const handleOnFocus = (
      event: React.FocusEvent<HTMLButtonElement | HTMLAnchorElement>
    ) => {
      const isFocusVisible = event.currentTarget.matches(':focus-visible');
      if (isFocusVisible) {
        setIsFocusedByTab(true);
      }
    };

    const handleOnBlur = () => setIsFocusedByTab(false);
    let Element: React.ReactElement;

    if (href && !disabled) {
      Element = (
        <a
          {...rest}
          ref={ref as ForwardedRef<HTMLAnchorElement>}
          className={cn(variants({ variant, size, disabled, isLink: true }))}
          href={href}
          onFocus={handleOnFocus}
          onBlur={handleOnBlur}
        >
          <Text asChild fontSize={fontSize}>
            <span>{children}</span>
          </Text>
        </a>
      );
    } else {
      Element = (
        <button
          {...rest}
          ref={ref as ForwardedRef<HTMLButtonElement>}
          className={cn(variants({ variant, size, disabled }), buttonClassName)}
          disabled={disabled}
          onFocus={handleOnFocus}
          onBlur={handleOnBlur}
        >
          {iconPrefix && (
            <div className={cn(iconVariants({ variant, disabled }))}>
              {iconPrefix}
            </div>
          )}
          <Text asChild fontSize={fontSize}>
            <span>{children}</span>
          </Text>
          {iconSuffix && (
            <div className={cn(iconVariants({ variant, disabled }))}>
              {iconSuffix}
            </div>
          )}
        </button>
      );
    }
    return (
      <div
        className={twMerge(
          'inline-block rounded-lg w-auto',
          isFocusedByTab && 'custom-focus-button-outline',
          className
        )}
      >
        {Element}
      </div>
    );
  }
);

export default Button;
export { Button, ButtonProps };
Button.displayName = 'Button';
